<template>
  <div class="page-admin-terminology">
    <BaseLoadingBar is-small v-if="pending" />
    <div class="page-admin-terminology__card">
      <h2 class="page-admin-terminology__title">{{ $t('page.admin-terminology.title') }}</h2>
      <p class="page-admin-terminology__description">{{ $t('page.admin-terminology.description') }}</p>
      <hr class="page-admin-terminology__separator" />

      <!-- Entity selector: scales to any number of renameable entities. Options use the stable
           canonical label (not the tenant's rename), so you always know which concept you're editing. -->
      <div class="page-admin-terminology__entity-field" v-if="entities.length">
        <label class="page-admin-terminology__label">{{ $t('page.admin-terminology.entity-label') }}</label>
        <FormSelect
          v-model="activeEntity"
          name="entity"
          :options="entityOptions"
          id-prop="value"
          label-prop="label"
          value-prop="value"
        />
        <p class="page-admin-terminology__entity-caption">{{ $t('page.admin-terminology.entity-caption') }}</p>
      </div>

      <template v-if="activeEntity">
        <!-- Locale tabs — a "default" badge marks locales still on the system default -->
        <div class="page-admin-terminology__tabs">
          <button
            v-for="locale in orderedLocales"
            :key="locale"
            type="button"
            class="page-admin-terminology__tab"
            :class="{ '--active': locale === activeLocale }"
            @click="activeLocale = locale"
          >
            {{ localeLabel(locale) }}
            <span v-if="!localeCustomized(locale)" class="page-admin-terminology__tab-badge">
              {{ $t('page.admin-terminology.locale-default-badge') }}
            </span>
          </button>
        </div>

        <!-- Non-blocking nudge when some (but not all) languages have been renamed -->
        <p class="page-admin-terminology__partial" v-if="isPartiallyLocalized">
          <BaseIcon name="info" class="page-admin-terminology__partial-icon" />
          <span>{{ $t('page.admin-terminology.partial-warning', { locales: defaultLocaleNames }) }}</span>
        </p>

        <div class="page-admin-terminology__body" v-if="form">
          <!-- Base inputs -->
          <div class="page-admin-terminology__inputs">
            <div class="page-admin-terminology__field">
              <label class="page-admin-terminology__label">{{ $t('page.admin-terminology.singular-label') }}</label>
              <FormInput
                v-model="form.singular"
                name="singular"
                type="text"
                :placeholder="$t('page.admin-terminology.singular-placeholder', activeLocale)"
                :maxlength="maxTermLength"
                is-compact
                @input="onBaseInputChange"
              />
            </div>

            <div class="page-admin-terminology__field">
              <label class="page-admin-terminology__label">{{ $t('page.admin-terminology.plural-label') }}</label>
              <FormInput
                v-model="form.plural"
                name="plural"
                type="text"
                :placeholder="$t('page.admin-terminology.plural-placeholder', activeLocale)"
                :maxlength="maxTermLength"
                is-compact
                @input="onBaseInputChange"
              />
            </div>

            <div class="page-admin-terminology__field" v-if="showGender">
              <label class="page-admin-terminology__label">{{ $t('page.admin-terminology.gender-label') }}</label>
              <FormSelect
                v-model="form.gender"
                name="gender"
                :options="genderOptions"
                id-prop="value"
                label-prop="label"
                value-prop="value"
                :placeholder="$t('page.admin-terminology.gender-placeholder')"
                @input="onBaseInputChange"
              />
            </div>

            <div class="page-admin-terminology__field">
              <div class="page-admin-terminology__label-row">
                <label class="page-admin-terminology__label">
                  {{ $t('page.admin-terminology.vowel-label') }}
                </label>
                <BaseToggle :value="!!form.startsWithVowelSound" @change="onToggleVowel" />
              </div>
              <p class="page-admin-terminology__hint">{{ $t('page.admin-terminology.vowel-hint') }}</p>
              <p class="page-admin-terminology__review" v-if="currentPreview && currentPreview.needsReview">
                <BaseIcon name="info" class="page-admin-terminology__review-icon" />
                <span>{{ $t('page.admin-terminology.vowel-review') }}</span>
              </p>
            </div>
          </div>

          <!-- Live preview of the effective forms; doubles as the advanced per-form editor when
               expanded. Most admins never open it — the 4 base inputs produce correct forms. -->
          <div class="page-admin-terminology__preview" v-if="currentPreview">
            <div class="page-admin-terminology__preview-head">
              <h3 class="page-admin-terminology__preview-title">{{ $t('page.admin-terminology.preview-title') }}</h3>
              <button type="button" class="page-admin-terminology__advanced-toggle" @click="toggleAdvanced">
                {{
                  advancedOpen ? $t('page.admin-terminology.advanced-hide') : $t('page.admin-terminology.advanced-show')
                }}
                <span v-if="overrideCount" class="page-admin-terminology__override-count">{{ overrideCount }}</span>
              </button>
            </div>
            <p class="page-admin-terminology__advanced-hint" v-if="advancedOpen">
              {{ $t('page.admin-terminology.advanced-hint') }}
            </p>
            <p class="page-admin-terminology__overrides-cleared" v-if="overridesCleared">
              <BaseIcon name="info" class="page-admin-terminology__partial-icon" />
              <span>{{ $t('page.admin-terminology.overrides-cleared') }}</span>
            </p>
            <!-- Forms laid out as label | Singular | Plural; pairs the 15 keys into rows. -->
            <div class="page-admin-terminology__preview-grid">
              <div class="page-admin-terminology__preview-corner"></div>
              <div class="page-admin-terminology__preview-colhead">
                {{ $t('page.admin-terminology.singular-label') }}
              </div>
              <div class="page-admin-terminology__preview-colhead">{{ $t('page.admin-terminology.plural-label') }}</div>
              <template v-for="pair in formPairs">
                <div class="page-admin-terminology__preview-rowlabel" :key="`${pair.base}|label`">
                  {{ pairLabel(pair.base) }}
                </div>
                <div
                  v-for="(key, idx) in [pair.singular, pair.plural]"
                  :key="`${pair.base}|${idx}`"
                  class="page-admin-terminology__preview-cell"
                  :class="{ '--overridden': key && isOverridden(key) }"
                >
                  <template v-if="key && advancedOpen">
                    <input
                      class="page-admin-terminology__preview-input"
                      :value="effectiveForm(key)"
                      :maxlength="maxTermLength"
                      @input="onOverrideInput(key, $event.target.value)"
                    />
                    <button
                      type="button"
                      class="page-admin-terminology__revert"
                      :class="{ '--hidden': !isOverridden(key) }"
                      :disabled="!isOverridden(key)"
                      :title="$t('page.admin-terminology.revert-title')"
                      @click="clearOverride(key)"
                    >
                      ↺
                    </button>
                  </template>
                  <template v-else-if="key">
                    <span class="page-admin-terminology__preview-value">{{ effectiveForm(key) }}</span>
                    <span v-if="isOverridden(key)" class="page-admin-terminology__edited-badge">
                      {{ $t('page.admin-terminology.edited-badge') }}
                    </span>
                  </template>
                </div>
              </template>
            </div>
          </div>

          <!-- Actions -->
          <div class="page-admin-terminology__actions">
            <BaseButton is-grey @click.native="requestReset" :disabled="saving || !hasStoredTerm">
              {{ $t('page.admin-terminology.reset') }}
            </BaseButton>
            <BaseButton @click.native="save" :disabled="saving || !isFormValid || !isDirty" :isLoading="saving">
              {{ $t('actions.save') }}
            </BaseButton>
          </div>
        </div>
      </template>

      <p class="page-admin-terminology__empty" v-if="!pending && !entities.length">
        {{ $t('page.admin-terminology.empty') }}
      </p>
    </div>

    <ConfirmationDialog
      ref="resetDialog"
      :buttonText="$t('page.admin-terminology.reset')"
      :is-danger="true"
      @execute="reset"
    >
      <template slot="content">
        {{
          $t('page.admin-terminology.reset-confirm-body', {
            entity: entityLabel(activeEntity),
            locale: localeLabel(activeLocale),
          })
        }}
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { LOCALE_VALUES } from '@/util/i18n/locale';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseButton from '@/components/base/BaseButton';
import BaseToggle from '@/components/base/BaseToggle';
import BaseIcon from '@/components/base/BaseIcon';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import FormInput from '@/components/form/FormInput';
import FormSelect from '@/components/form/FormSelect';

const PREVIEW_DEBOUNCE_MS = 300;
// Backend stores term forms as varchar(255); cap inputs so the UI can't produce an over-length
// request (the backend should also reject >255 with a clean 400 rather than a DB-error 500).
const MAX_TERM_LENGTH = 255;

export default {
  name: 'PageAdminTerminology',
  components: { BaseLoadingBar, BaseButton, BaseToggle, BaseIcon, ConfirmationDialog, FormInput, FormSelect },
  data() {
    return {
      activeEntity: null,
      activeLocale: null,
      editable: {}, // keyed `${entity}|${locale}` -> { singular, plural, gender, startsWithVowelSound, touched }
      previews: {}, // keyed `${entity}|${locale}` -> preview response
      previewTimer: null,
      lastPreviewSignature: null, // dedupes identical preview triggers (e.g. created + watcher on load)
      advancedOpen: false, // "edit individual forms" affordance, collapsed by default
      overridesCleared: false, // shows a notice when a base-input change wiped manual edits
      maxTermLength: MAX_TERM_LENGTH,
    };
  },
  computed: {
    ...mapGetters('adminTerminology', ['entities', 'locales', 'formKeys', 'termFor', 'pending', 'saving']),
    currentKey() {
      return `${this.activeEntity}|${this.activeLocale}`;
    },
    form() {
      return this.editable[this.currentKey] || null;
    },
    currentPreview() {
      return this.previews[this.currentKey] || null;
    },
    showGender() {
      // English gender is fixed ("n") and irrelevant to rendering; only French exposes it.
      return this.activeLocale === 'fr-CA';
    },
    entityOptions() {
      return this.entities.map((entity) => ({ label: this.entityLabel(entity), value: entity }));
    },
    // Display locales in the app's canonical order (French first), independent of the order the
    // backend returns them in. Any unknown locale sorts after the known ones.
    orderedLocales() {
      const rank = (locale) => {
        const idx = LOCALE_VALUES.indexOf(locale);
        return idx === -1 ? LOCALE_VALUES.length : idx;
      };
      return [...this.locales].sort((a, b) => rank(a) - rank(b));
    },
    genderOptions() {
      return [
        { label: this.$t('page.admin-terminology.gender-masculine'), value: 'm' },
        { label: this.$t('page.admin-terminology.gender-feminine'), value: 'f' },
      ];
    },
    hasStoredTerm() {
      return !!this.termFor(this.activeEntity, this.activeLocale);
    },
    overrideCount() {
      return this.form ? Object.keys(this.form.overrides).length : 0;
    },
    // Pair the flat formKeys into { base, singular, plural } rows for the two-column preview,
    // preserving the backend's order. `no_singular` has no plural counterpart (plural stays null).
    formPairs() {
      const byBase = {};
      const pairs = [];
      this.formKeys.forEach((key) => {
        let base;
        let number;
        if (key === 'singular' || key === 'plural') {
          base = '';
          number = key;
        } else if (key.endsWith('_singular')) {
          base = key.slice(0, -'_singular'.length);
          number = 'singular';
        } else if (key.endsWith('_plural')) {
          base = key.slice(0, -'_plural'.length);
          number = 'plural';
        } else {
          base = key;
          number = 'singular';
        }
        if (!byBase[base]) {
          byBase[base] = { base, singular: null, plural: null };
          pairs.push(byBase[base]);
        }
        byBase[base][number] = key;
      });
      return pairs;
    },
    customizedLocales() {
      return this.orderedLocales.filter((locale) => this.localeCustomized(locale));
    },
    // Some — but not all — supported locales have been renamed: surface the nudge.
    isPartiallyLocalized() {
      const count = this.customizedLocales.length;
      return count > 0 && count < this.orderedLocales.length;
    },
    defaultLocaleNames() {
      return this.orderedLocales
        .filter((locale) => !this.localeCustomized(locale))
        .map((locale) => this.localeLabel(locale))
        .join(', ');
    },
    isFormValid() {
      if (!this.form) return false;
      const hasNames = !!String(this.form.singular || '').trim() && !!String(this.form.plural || '').trim();
      // French requires a grammatical gender; English is fixed to "n".
      const hasGender = this.activeLocale === 'en-CA' || this.form.gender === 'm' || this.form.gender === 'f';
      return hasNames && hasGender;
    },
    // True when the form differs from the saved term, so Save is only enabled when there's
    // something to persist. A brand-new (unsaved) term is "dirty" as soon as it's valid.
    isDirty() {
      if (!this.form) return false;
      const stored = this.termFor(this.activeEntity, this.activeLocale);
      if (!stored) return true;
      if (this.form.singular !== stored.singular) return true;
      if (this.form.plural !== stored.plural) return true;
      if (this.form.gender !== stored.gender) return true;
      if (!!this.form.startsWithVowelSound !== !!stored.startsWithVowelSound) return true;
      return !this.overridesEqual(this.form.overrides, stored.overrides || {});
    },
    // Drives the preview watcher; recomputes when any input or the active tab changes.
    previewSignature() {
      return this.signatureFor(this.currentKey, this.form);
    },
  },
  watch: {
    previewSignature() {
      this.schedulePreview();
    },
  },
  methods: {
    ...mapActions('adminTerminology', ['getSnapshot', 'preview', 'saveTerm', 'resetTerm']),
    humanize(key) {
      return String(key).replace(/_/g, ' ');
    },
    // Row label for a form pair (the bare noun, or an article/preposition group). Resolved in the
    // locale being edited (the active tab), so the article examples match the forms shown —
    // "le / la" above French forms, "the" above English forms — rather than the user's UI language.
    // Falls back to a humanized id for any base without a translation.
    pairLabel(base) {
      const id = base || 'noun';
      const key = `page.admin-terminology.form-label.${id}`;
      const label = this.$t(key, this.activeLocale);
      return label === key ? this.humanize(id) : label;
    },
    // Stable, localized reference name for the entity (NOT tokenized), so it stays constant
    // regardless of the tenant's rename. Falls back to a humanized id for unknown entities.
    entityLabel(entity) {
      if (!entity) return ''; // avoid an "entity.null" i18n lookup before the snapshot loads
      const key = `page.admin-terminology.entity.${entity}`;
      const label = this.$t(key);
      return label === key ? this.humanize(entity) : label;
    },
    localeLabel(locale) {
      if (!locale) return ''; // avoid a "locale-null" i18n lookup before the snapshot loads
      const key = `page.admin-terminology.locale-${locale}`;
      const label = this.$t(key);
      return label === key ? locale : label;
    },
    // Whether a saved (effective) customization exists for this entity/locale — drives the
    // "default" tab badge and the partial-localization nudge. Reflects saved state, not drafts.
    localeCustomized(locale) {
      return !!this.termFor(this.activeEntity, locale);
    },
    onToggleVowel(value) {
      if (!this.form) return;
      this.form.startsWithVowelSound = value;
      this.form.touched = true; // user made an explicit choice; stop auto-suggesting
      this.clearOverridesOnBaseChange(); // elision/agreement forms changed
    },
    // Effective value for a form key: a manual override if present, otherwise the generated form.
    effectiveForm(key) {
      const override = this.form && this.form.overrides[key];
      if (override != null) return override;
      return this.currentPreview ? this.currentPreview.forms[key] : '';
    },
    isOverridden(key) {
      return !!(this.form && this.form.overrides[key] != null);
    },
    // Record a manual edit as a sparse override. Reverts to generated when cleared or set back to
    // the generated value (so we never send a redundant or blank override — the backend 400s on blank).
    onOverrideInput(key, value) {
      if (!this.form) return;
      this.overridesCleared = false;
      const generated = this.currentPreview ? this.currentPreview.forms[key] : '';
      if (value === '' || value === generated) {
        this.$delete(this.form.overrides, key);
      } else {
        this.$set(this.form.overrides, key, value);
      }
    },
    clearOverride(key) {
      if (this.form) this.$delete(this.form.overrides, key);
    },
    overridesEqual(a, b) {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      return aKeys.length === bKeys.length && aKeys.every((k) => a[k] === b[k]);
    },
    // Overrides are sticky on the backend, so changing a base input would otherwise keep stale
    // hand-edits. Clear them on any base change so a stale override can't silently ship.
    onBaseInputChange() {
      this.clearOverridesOnBaseChange();
    },
    clearOverridesOnBaseChange() {
      if (this.form && Object.keys(this.form.overrides).length) {
        this.form.overrides = {};
        this.overridesCleared = true;
      }
    },
    toggleAdvanced() {
      this.advancedOpen = !this.advancedOpen;
      this.overridesCleared = false;
    },
    buildEditable() {
      const editable = {};
      this.entities.forEach((entity) => {
        this.locales.forEach((locale) => {
          const term = this.termFor(entity, locale);
          editable[`${entity}|${locale}`] = term
            ? {
                singular: term.singular,
                plural: term.plural,
                gender: term.gender,
                startsWithVowelSound: term.startsWithVowelSound,
                touched: true,
                overrides: { ...(term.overrides || {}) }, // sparse manual per-form corrections
              }
            : {
                singular: '',
                plural: '',
                // No gender pre-selected for French — the admin makes a deliberate choice.
                gender: locale === 'en-CA' ? 'n' : '',
                startsWithVowelSound: false,
                touched: false, // let the backend auto-suggest until the admin edits the toggle
                overrides: {},
              };
        });
      });
      this.editable = editable;
      this.previews = {};
      this.lastPreviewSignature = null;
      this.overridesCleared = false;
    },
    // Dedup signature for a specific (tab key, form). A method so runPreview can compute it from
    // its captured key+form rather than the live tab.
    signatureFor(key, form) {
      if (!form) return '';
      return [key, form.singular, form.plural, form.gender, form.startsWithVowelSound, form.touched].join('|');
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.runPreview(), PREVIEW_DEBOUNCE_MS);
    },
    async runPreview() {
      const form = this.form;
      if (!form || !this.isFormValid) return;
      // Capture the tab up front — the active tab can change during the await.
      const key = this.currentKey;
      // Skip if nothing relevant changed (collapses the created() + watcher triggers on load).
      const signature = this.signatureFor(key, form);
      if (signature === this.lastPreviewSignature) return;
      this.lastPreviewSignature = signature;
      try {
        const result = await this.preview({
          locale: this.activeLocale,
          singular: form.singular,
          plural: form.plural,
          gender: this.activeLocale === 'en-CA' ? 'n' : form.gender,
          // Until the admin touches the toggle, send null so the backend auto-suggests.
          startsWithVowelSound: form.touched ? form.startsWithVowelSound : null,
          overrides: {},
        });
        this.$set(this.previews, key, result);
        // Reflect the backend's suggestion in the toggle while the admin hasn't overridden it.
        if (!form.touched && typeof result.effectiveStartsWithVowelSound === 'boolean') {
          form.startsWithVowelSound = result.effectiveStartsWithVowelSound;
          // Use the captured key+form, not the live previewSignature, which may now be a different tab.
          this.lastPreviewSignature = this.signatureFor(key, form);
        }
      } catch (e) {
        // Clear our marker so the same inputs can be retried (only if a concurrent preview
        // hasn't replaced it in the meantime).
        if (this.lastPreviewSignature === signature) this.lastPreviewSignature = null;
        this.$toast.error(e);
      }
    },
    async save() {
      if (!this.isFormValid) return;
      try {
        await this.saveTerm({
          entityId: this.activeEntity,
          locale: this.activeLocale,
          input: {
            singular: this.form.singular,
            plural: this.form.plural,
            gender: this.activeLocale === 'en-CA' ? 'n' : this.form.gender,
            startsWithVowelSound: this.form.startsWithVowelSound,
            overrides: { ...this.form.overrides }, // sparse — only manually corrected form keys
          },
        });
        this.form.touched = true;
        this.$toast.success(this.$t('page.admin-terminology.save-success'));
      } catch (e) {
        this.$toast.error(e);
      }
    },
    requestReset() {
      this.$refs.resetDialog.openDialog();
    },
    // Invoked by DeleteDialog's "execute" event; the dialog closes itself.
    async reset() {
      try {
        await this.resetTerm({ entityId: this.activeEntity, locale: this.activeLocale });
        this.$toast.success(this.$t('page.admin-terminology.reset-success'));
        this.buildEditable();
        this.runPreview();
      } catch (e) {
        this.$toast.error(e);
      }
    },
  },
  async created() {
    try {
      await this.getSnapshot();
      this.activeEntity = this.entities[0] || null;
      this.activeLocale = this.orderedLocales[0] || null;
      this.buildEditable();
      this.runPreview();
    } catch (e) {
      this.$toast.error(e);
    }
  },
  beforeDestroy() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  },
};
</script>

<style lang="scss">
.page-admin-terminology {
  padding: 32px;

  &__card {
    background: white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
    max-width: 760px;
    padding: 32px;
  }

  &__title {
    color: $color-normal;
    font-size: $font-size-xxbigger;
    margin: 0;
  }

  &__description {
    color: $color-neutral;
    font-size: 13px;
    margin: 8px 0 0;
  }

  &__separator {
    border: none;
    border-top: 1px solid $color-border;
    margin: 24px 0;
  }

  &__tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
  }

  &__entity-field {
    max-width: 320px;
    margin-bottom: 24px;
  }

  &__entity-caption {
    font-size: 12px;
    color: $color-neutral;
    margin: 6px 0 0;
  }

  &__empty {
    font-size: 14px;
    color: $color-neutral;
  }

  &__tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 6px 12px;
    font-size: 14px;
    color: $color-neutral;
    cursor: pointer;

    &.--active {
      color: $color-normal;
      border-bottom-color: $color-accent;
      font-weight: 600;
    }
  }

  &__tab-badge {
    margin-left: 6px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: $color-neutral;
    border: 1px solid $color-border;
    border-radius: 8px;
    padding: 0 6px;
    font-weight: 400;
  }

  &__partial {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $color-orange-600;
    margin: 0 0 20px;
  }

  &__partial-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &__field {
    margin-bottom: 20px;
  }

  &__label {
    display: block;
    font-size: 14px;
    color: $color-normal;
    margin-bottom: 6px;
  }

  &__label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__hint {
    font-size: 12px;
    color: $color-neutral;
    margin: 6px 0 0;
  }

  &__review {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $color-orange-600;
    margin: 6px 0 0;
  }

  &__review-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &__preview {
    background: $color-bg;
    border-radius: $border-radius-root;
    padding: 16px;
    margin: 8px 0 24px;
  }

  &__preview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__preview-title {
    font-size: 14px;
    color: $color-normal;
    margin: 0;
  }

  &__advanced-toggle {
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    color: $color-accent;
    cursor: pointer;
  }

  &__override-count {
    margin-left: 4px;
    font-size: 10px;
    background: $color-accent;
    color: white;
    border-radius: 8px;
    padding: 0 6px;
  }

  &__advanced-hint {
    font-size: 12px;
    color: $color-neutral;
    margin: 0 0 12px;
  }

  &__overrides-cleared {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $color-orange-600;
    margin: 0 0 12px;
  }

  &__preview-input {
    width: 180px;
    font-size: 13px;
    color: $color-normal;
    border: 1px solid $color-input-border;
    border-radius: 4px;
    padding: 2px 6px;
  }

  &__revert {
    background: none;
    border: none;
    cursor: pointer;
    color: $color-neutral;
    font-size: 14px;
    line-height: 1;
    padding: 0 4px;

    // Always reserve the slot so toggling an override doesn't shift the columns.
    &.--hidden {
      visibility: hidden;
    }
  }

  &__edited-badge {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: $color-orange-600;
    align-self: center;
  }

  &__preview-grid {
    // Content-sized value columns keep the singular/plural of a row visually paired, rather than
    // stretching apart. The label column gets extra trailing space to separate it from the pair.
    display: grid;
    grid-template-columns: max-content max-content max-content;
    align-items: center;
    column-gap: 16px;
    row-gap: 4px;
  }

  &__preview-colhead {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: $color-neutral;
    padding-bottom: 4px;
  }

  &__preview-rowlabel {
    font-size: 13px;
    color: $color-neutral;
    padding-right: 16px;
  }

  &__preview-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    min-width: 0;

    &.--overridden .page-admin-terminology__preview-value,
    &.--overridden .page-admin-terminology__preview-input {
      color: $color-orange-600;
    }
  }

  &__preview-value {
    color: $color-normal;
    font-weight: 500;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
  }
}
</style>
